
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { classYears } from '@/data/classYears';
import { Search, X } from 'lucide-react';
import { 
  Dialog,
  DialogContent,
  DialogClose
} from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { Image } from '@/components/ui/image';

interface ClassYearImageProps {
  selectedClassYear: string | null;
}

const ClassYearImage: React.FC<ClassYearImageProps> = ({ selectedClassYear }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [selectedClass, setSelectedClass] = useState<typeof classYears[0] | undefined>(undefined);
  
  useEffect(() => {
    if (selectedClassYear) {
      const foundClass = classYears.find(c => c.value === selectedClassYear);
      setSelectedClass(foundClass);
    } else {
      setSelectedClass(undefined);
    }
  }, [selectedClassYear]);
  
  if (!selectedClass || !selectedClass.image) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-200 rounded-lg">
        <p className="text-gray-500">Không có ảnh</p>
      </div>
    );
  }
  
  return (
    <AnimatePresence>
      <motion.div
        key={selectedClass.value}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.5 }}
        className={`bg-white ${isMobile ? 'p-3 rounded-lg shadow' : 'p-6 rounded-xl shadow-lg'} h-full`}
      >
        <h2 className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold ${isMobile ? 'mb-2' : 'mb-4'} text-blue-700`}>
          Ảnh lớp {selectedClass.value}
        </h2>
        
        <motion.div 
          className={`rounded-xl overflow-hidden bg-gray-100 relative group cursor-pointer ${isMobile ? 'h-28' : 'h-[200px]'}`}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
          onClick={() => setIsImageModalOpen(true)}
          role="button"
          aria-label="Xem ảnh đầy đủ"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsImageModalOpen(true);
            }
          }}
        >
          <div className="relative w-full h-full border-4 border-[#D3E4FD] rounded-lg p-1 shadow-inner">
            <div className="absolute inset-0 border-2 border-blue-300 rounded-md pointer-events-none" />
            <Image 
              src={selectedClass.image} 
              alt={`Lớp ${selectedClass.value}`}
              className="w-full h-full object-cover rounded-lg shadow transition-transform duration-300"
              fallback={
                <div className="flex items-center justify-center h-full bg-gray-200 animate-pulse rounded-lg">
                  <p className="text-gray-500">Đang tải ảnh...</p>
                </div>
              }
            />
            
            {/* Magnifying glass icon that shows on hover */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg">
              <Search className="h-10 w-10 text-white drop-shadow-md" />
            </div>
          </div>
        </motion.div>
        
        {/* Full image modal */}
        <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
          <DialogContent className="max-w-4xl w-[90vw] p-1 sm:p-2 md:p-4 bg-white rounded-lg">
            <div className="relative w-full h-full">
              {/* Removed the DialogClose component here since it's already included by default in the DialogContent */}
              
              <div className="w-full h-full flex items-center justify-center overflow-auto max-h-[80vh] border-8 border-[#D3E4FD] rounded-xl p-2">
                <div className="relative w-full h-full border-2 border-blue-300 rounded-lg overflow-hidden">
                  <img 
                    src={selectedClass.image} 
                    alt={`Lớp ${selectedClass.value} - Xem đầy đủ`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </AnimatePresence>
  );
};

export default ClassYearImage;
